import type { SxProps, Theme } from "@mui/material";
import { Box, Stack, useTheme } from "@mui/material";
import type { PropsWithChildren } from "react";

interface Props {
  sx?: SxProps<Theme>;
}

const pageStyle = (theme: Theme) => ({
  position: "relative",
  isolation: "isolate",
  height: "100%",
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.background.grey,
});

const StyledPage = ({ sx, children }: PropsWithChildren<Props>) => {
  const { palette } = useTheme();
  const cutout = { fill: palette.background.grey, stroke: palette.secondary.light };

  return (
    <Stack spacing={3} useFlexGap sx={[pageStyle, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Box sx={{ position: "absolute", inset: 0, zIndex: -1, clipPath: "inset(0)" }}>
        <Box
          component="svg"
          viewBox="0 0 1440 1024"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
          aria-hidden
          sx={{ position: "fixed", inset: 0, width: "100%", height: "100%" }}
        >
          <g stroke={palette.divider} strokeMiterlimit="10">
            <path d="M131.599 65.5996L73.9995 126.1L131.599 186.6L355.999 186.6" />
            <path d="M131.6 65.5995L695.8 65.5996L819.999 -51.5" />
            <path d="M695.8 75.5996L131.6 75.5996" strokeDasharray="2 2" />
            <path d="M74.0203 126.026L-3.05359 48.9521L-80.1275 126.026L-3.0536 203.1L74.0203 126.026Z" />
            <path
              d="M-3.1235 61.6764L-67.4696 126.022L-3.12351 190.369L61.2226 126.022L-3.1235 61.6764Z"
              strokeDasharray="2 2"
            />
            <path d="M57.3995 -8.80017L57.3996 -471L-63.6004 -471L-63.6005 -8.80018L-3.10046 48.7998L57.3995 -8.80017Z" />
            <path d="M-3.10093 203L57.399 260.6L57.3993 474" />
            <path d="M1281.6 838.2H1366.5H1392.9H1608.1H1743.8V959.2H1281.6L1224 898.7L1281.6 838.2Z" />
            <path d="M1012.2 959.2L1069.8 898.7L1012.2 838.2H913.9" />
            <path d="M448 959.2H1012.2" />
            <path d="M448 949.2H1012.2" strokeDasharray="2 2" />
            <path d="M1069.78 898.774L1146.85 975.848L1223.93 898.774L1146.85 821.7L1069.78 898.774Z" />
            <path
              d="M1146.92 963.123L1211.27 898.777L1146.92 834.431L1082.58 898.777L1146.92 963.123Z"
              strokeDasharray="2 2"
            />
            <path d="M1086.4 1033.6V1495.8H1207.4V1033.6L1146.9 976L1086.4 1033.6Z" />
            <path d="M1146.9 821.8L1086.4 764.2V664.7" />
            <path d="M1207.4 764.2L1146.9 821.8" />
            <path d="M1207.4 200V764.2" />
            <path d="M1197.4 200V764.2" strokeDasharray="2 2" />
          </g>
          <circle cx="193.192" cy="187.192" r="7.19202" fill={palette.primary.main} />
          <circle cx="635.608" cy="65.6078" r="6.69202" {...cutout} />
          <rect
            x="47.7061"
            y="335.811"
            width="12.8736"
            height="12.8736"
            transform="rotate(-45 47.7061 335.811)"
            {...cutout}
          />
          <circle cx="1040" cy="929" r="6" fill={palette.primary.main} />
          <rect
            x="1304.5"
            y="844.374"
            width="12.8736"
            height="12.8736"
            transform="rotate(-90 1304.5 844.374)"
            {...cutout}
          />
          <circle cx="508.192" cy="959.192" r="6.69202" {...cutout} />
          <path d="M1207.5 456L1215.73 470.25H1199.27L1207.5 456Z" fill={palette.primary.main} />
        </Box>
      </Box>
      {children}
    </Stack>
  );
};

export default StyledPage;
